import React, { useCallback, useEffect, useState } from 'react';
import { CalendarCheck, Download, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', slate: '#5C6B7A', green: '#1D9E75', amber: '#B27516', red: '#E24B4A', bg: '#F4F6F9' };
const LEVEL = { stop: C.red, warn: C.amber, info: C.blue, ok: C.green };
const fmt = (v, s = '') => (v == null ? '—' : `${v}${s}`);
const ts = (s) => (s ? `${s.slice(0, 16).replace('T', ' ')}Z` : '—');

function Kpi({ label, value, testid }) {
  return (
    <div className="rounded-sm px-3 py-2" style={{ background: C.bg }} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wide" style={{ color: C.slate }}>{label}</p>
      <p className="text-xs font-semibold mt-0.5 whitespace-normal" style={{ color: C.navy }}>{value}</p>
    </div>
  );
}

function Attention({ items }) {
  return (
    <ul className="mt-2 space-y-1" data-testid="scorecard-attention">
      {items.map((i, n) => (
        <li key={n} className="text-[11px] flex gap-2 whitespace-normal" data-testid={`scorecard-attention-${i.level}-${n}`}>
          <span className="inline-block px-1.5 rounded-sm text-[9px] font-semibold text-white shrink-0 h-4 leading-4" style={{ background: LEVEL[i.level] }}>{i.level}</span>
          <span style={{ color: C.navy }}>{i.text}</span>
        </li>
      ))}
    </ul>
  );
}

function AgentTable({ rec }) {
  const traces = Object.fromEntries(rec.traces.agents.map(a => [a.agent, a]));
  const budget = Object.fromEntries(rec.budget.agents.map(a => [a.agent, a]));
  return (
    <table className="w-full mt-2 text-[10px] table-fixed" data-testid="scorecard-agents-table">
      <thead><tr style={{ color: C.slate }}>
        <th className="text-left py-1 w-[16%]">agent</th><th className="text-left w-[22%]">evals</th><th className="text-left w-[10%]">Δ week</th>
        <th className="text-left w-[22%]">calls · err %</th><th className="text-left w-[16%]">p50 / p95</th><th className="text-left w-[14%]">budget</th>
      </tr></thead>
      <tbody>
        {rec.evals.agents.map(a => {
          const t = traces[a.agent]; const b = budget[a.agent];
          const verdictColor = a.verdict === 'pass' ? C.green : a.verdict === 'blocked' ? C.red : C.slate;
          return (
            <tr key={a.agent} className="border-t align-top" style={{ borderColor: 'rgba(136,150,168,0.15)' }} data-testid={`scorecard-agent-${a.agent}`}>
              <td className="py-1 pr-2 font-semibold whitespace-normal" style={{ color: C.navy }}>{a.agent}</td>
              <td className="py-1 pr-2 whitespace-normal"><span className="font-semibold" style={{ color: verdictColor }}>{a.verdict}</span>
                {a.pass_rate != null && <span className="font-mono" style={{ color: C.slate }}> · {a.pass_rate} % · {a.trials} trials · {a.runs} run{a.runs === 1 ? '' : 's'}</span>}</td>
              <td className="py-1 pr-2 font-mono whitespace-normal" style={{ color: a.delta_points == null ? C.slate : a.delta_points < 0 ? C.red : C.green }}>{a.delta_points == null ? '—' : `${a.delta_points > 0 ? '+' : ''}${a.delta_points} pts`}</td>
              <td className="py-1 pr-2 font-mono whitespace-normal" style={{ color: C.slate }}>{t ? `${t.calls} · ${fmt(t.error_rate_pct, ' %')}${t.flagged ? ` · ${t.flagged} flagged` : ''}` : 'no calls'}</td>
              <td className="py-1 pr-2 font-mono whitespace-normal" style={{ color: C.slate }}>{t && t.p50_ms != null ? `${(t.p50_ms / 1000).toFixed(1)} / ${(t.p95_ms / 1000).toFixed(1)} s` : '—'}</td>
              <td className="py-1 font-mono whitespace-normal" style={{ color: b?.level === 'stop' ? C.red : b?.level === 'warning' ? C.amber : C.slate }}>{b ? `${b.pct == null ? `${b.usage.calls} calls · no limit set` : `${b.pct} % of limit`}${b.kill ? ' · off' : ''}${b.breaker?.state === 'open' ? ' · breaker open' : ''}` : '—'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function Humans({ h }) {
  const o = h.overrides, r = h.referrals, p = h.pipeline, a = h.aria, d = h.dean;
  const join = (obj) => Object.entries(obj || {}).sort((x, y) => y[1] - x[1]).map(([k, v]) => `${k} ${v}`).join(', ') || 'none';
  return (
    <ul className="mt-2 space-y-1 text-[11px] whitespace-normal" style={{ color: C.navy }} data-testid="scorecard-humans">
      <li data-testid="scorecard-overrides"><b>Underwriting overrides</b> — {o.count} on {o.manual_decisions} manual decisions ({fmt(o.rate_pct, ' %')}{o.qa_excluded || o.system_excluded ? `; ${o.qa_excluded || 0} QA/demo and ${o.system_excluded || 0} system rows excluded` : ''}) · {join(o.by_direction)}{o.top_pairs?.length ? ` · top: ${o.top_pairs.slice(0, 3).map(x => `${x.pair} ×${x.n}`).join('; ')}` : ''}</li>
      {o.attribution && (
        <li data-testid="scorecard-refer-attribution"><b>REFER reversals read apart</b> — score-driven {o.attribution.score_driven.refer_approve} approved / {o.attribution.score_driven.refer_decline} declined{o.attribution.score_driven.approve_pct != null ? ` (${o.attribution.score_driven.approve_pct} % approved)` : ''} · rule-driven {o.attribution.rule_driven.refer_approve} approved / {o.attribution.rule_driven.refer_decline} declined
          {o.attribution.by_rule?.length ? <> — rules humans reverse: {o.attribution.by_rule.slice(0, 4).map(r => `${r.rule} ${r.approved}/${r.approved + r.declined} approved`).join('; ')}</> : null}</li>
      )}
      <li data-testid="scorecard-referrals"><b>Referrals</b> — {r.referred} of {r.scored} scored submissions ({fmt(r.rate_pct, ' %')}) · {join(r.by_decision)}</li>
      <li data-testid="scorecard-pipeline"><b>Email-to-quote pipeline</b> — {p.runs} runs · STP {fmt(p.stp_pct, ' %')} · reviewed {fmt(p.reviewed_pct, ' %')} · {join(p.by_outcome)}</li>
      <li data-testid="scorecard-aria"><b>ARIA in the Pen gate</b> — renewals reviewed {join(a.renewals)} (escalate {fmt(a.renewals_escalate_pct, ' %')}) · {a.auto_offers} auto-offered without a review · claims mismatch {join(a.claims_mismatch)}</li>
      <li data-testid="scorecard-dean"><b>Dean</b> — {d.proposed} proposed · {d.confirmed} confirmed · {d.corrected} corrected · agreement {fmt(d.agreement_pct, ' %')} (C8 asks 90)</li>
    </ul>
  );
}

export default function ScorecardCard({ api }) {
  const { user } = useAuth();
  const canRun = ['platform_owner', 'mga_admin'].includes(user?.role);
  const [data, setData] = useState(null);
  const [rec, setRec] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const [forbidden, setForbidden] = useState(false);
  const load = useCallback(() => api.get('/assurance/scorecard').then(r => { setData(r.data); setRec(r.data.latest); setFailed(false); })
    .catch(e => { if (e.response?.status === 403) setForbidden(true); else setFailed(true); }), [api]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try { const r = await api.post('/assurance/scorecard/run'); setRec(r.data); await load(); toast.success(`Scorecard ${r.data.week} written`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Scorecard not generated'); }
    finally { setBusy(false); }
  };
  const open = (id) => api.get(`/assurance/scorecard/${id}`).then(r => setRec(r.data)).catch(() => toast.error('Scorecard not found'));
  const download = async () => {
    try {
      const r = await api.get(`/assurance/scorecard/${rec.scorecard_id}/markdown`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = url; a.download = `scorecard-${rec.week}.md`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  if (forbidden || (!data && !failed)) return null;
  const h = rec?.headline;
  return (
    <div className="rounded-sm border bg-white p-4 mt-4 whitespace-normal" style={{ borderColor: 'rgba(136,150,168,0.25)' }} data-testid="scorecard-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: C.navy }}><CalendarCheck className="w-4 h-4" style={{ color: C.blue }} /> Weekly scorecard{rec ? ` · ${rec.week}` : ''}</h3>
          <p className="text-[11px] mt-1" style={{ color: C.slate }} data-testid="scorecard-window">
            {rec ? <>Seven days {ts(rec.window.since)} → {ts(rec.window.until)} · written {ts(rec.generated_at)} by {rec.trigger} · no model call made to produce it</> : 'No scorecard yet — the Monday weekly-eval tick writes the first one, or generate it now.'}
            {failed ? ' · last refresh failed — showing the previous reading' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {data?.history?.length > 1 && (
            <select value={rec?.scorecard_id || ''} onChange={e => open(e.target.value)} className="text-[11px] border rounded-sm px-2 py-1 w-72 max-w-full" style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }} data-testid="scorecard-history-select">
              {data.history.map(x => <option key={x.scorecard_id} value={x.scorecard_id}>{`${x.week} · ${ts(x.generated_at)} · ${x.trigger}`}</option>)}
            </select>
          )}
          {rec && <button onClick={download} className="flex items-center gap-1 text-[11px] px-2 py-1 border rounded-sm" style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.blue }} data-testid="scorecard-download-btn"><Download className="w-3 h-3" /> .md</button>}
          {canRun && <button onClick={generate} disabled={busy} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-sm text-white disabled:opacity-60" style={{ background: C.blue }} data-testid="scorecard-run-btn">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Generate now</button>}
        </div>
      </div>

      {rec && (<>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3">
          <Kpi label="Evals" value={h.evals} testid="scorecard-kpi-evals" />
          <Kpi label="Model calls" value={h.calls} testid="scorecard-kpi-calls" />
          <Kpi label="Tokens" value={h.tokens} testid="scorecard-kpi-tokens" />
          <Kpi label="Overrides" value={h.overrides} testid="scorecard-kpi-overrides" />
          <Kpi label="Referrals" value={h.referrals} testid="scorecard-kpi-referrals" />
        </div>

        <p className="text-xs font-semibold mt-4" style={{ color: C.navy }}>Needs a decision <span className="font-normal" style={{ color: C.slate }}>· {h.attention} item{h.attention === 1 ? '' : 's'}</span></p>
        <Attention items={rec.attention} />

        <p className="text-xs font-semibold mt-4" style={{ color: C.navy }}>Agents <span className="font-normal" style={{ color: C.slate }}>· evals, traces and budget side by side</span></p>
        <AgentTable rec={rec} />
        {rec.champion_gate && <p className="text-[10px] font-mono mt-1 whitespace-normal" style={{ color: C.slate }} data-testid="scorecard-champion-gate">champion gate {rec.champion_gate.run_id}: {Object.entries(rec.champion_gate.archetypes).map(([k, v]) => `${k} ${fmt(v.score)}${v.drop ? ' ↓' : ''}`).join(' · ')}</p>}

        <p className="text-xs font-semibold mt-4" style={{ color: C.navy }}>Humans against the agents</p>
        <Humans h={rec.humans} />

        <p className="text-[10px] mt-3 font-mono whitespace-normal" style={{ color: C.slate }} data-testid="scorecard-jobs">
          bounded jobs: {rec.jobs.runs} runs, {rec.jobs.failed} failed · {rec.jobs.jobs.map(j => `${j.job} ${j.runs}`).join(' · ') || 'none'}
          {' · '}pins: {Object.entries(rec.pins).map(([s, p]) => `${s} ${p.prompt_version}`).join(', ')}
        </p>
      </>)}
    </div>
  );
}
